import { Controller } from "@hotwired/stimulus"

// Generic right-hand slide-over built on the native <dialog> element. Put
// data-controller="drawer" on a wrapper that holds both the launcher(s) and the
// dialog, mark the dialog with data-drawer-target="dialog", and give the dialog a
// `translate-x-full` (offscreen) class plus a `transition-transform` so opening
// and closing animate.
//
//   <div data-controller="drawer"
//        data-action="drawer:close-requested->drawer#requestClose">
//     <button data-action="drawer#open">Open</button>
//     <dialog data-drawer-target="dialog"
//             data-action="cancel->drawer#cancel close->drawer#closed"
//             class="… translate-x-full transition-transform">…</dialog>
//   </div>
//
// Close it from a Turbo Stream with turbo_power's dispatch_event, optionally
// holding a server-rendered acknowledgement first via detail.delay (ms):
//   turbo_stream.dispatch_event("#my-drawer", "drawer:close-requested", detail: {delay: 1000})
//
// With data-drawer-reset-on-close-value="true", closing returns the drawer to a
// pristine state: contained <form>s are reset and any [data-drawer-reset] slots
// are emptied.
export default class extends Controller {
  static targets = ["dialog"]

  static values = {
    resetOnClose: { type: Boolean, default: false },
  }

  disconnect() {
    clearTimeout(this.closeTimer)
    clearTimeout(this.closeFallback)
  }

  open() {
    // Cancel any pending close (a prior success still counting down, or a
    // slide-out the user interrupted by reopening) and don't re-open an
    // already-open dialog — showModal() throws then.
    clearTimeout(this.closeTimer)
    this.cancelClose()
    if (!this.dialogTarget.open) this.dialogTarget.showModal()
    requestAnimationFrame(() => this.dialogTarget.classList.remove("translate-x-full"))
  }

  // Esc fires the dialog `cancel` event; the close button and Cancel land here too.
  cancel(event) {
    event?.preventDefault()
    this.close()
  }

  // Wired to a dispatched `drawer:close-requested` event; honors detail.delay (ms).
  requestClose(event) {
    clearTimeout(this.closeTimer)
    this.closeTimer = setTimeout(() => this.close(), event?.detail?.delay || 0)
  }

  close() {
    if (!this.dialogTarget.open || this.finishClose) return

    this.dialogTarget.classList.add("translate-x-full")
    // Close once the slide-out finishes — but never depend solely on
    // transitionend: it doesn't fire under prefers-reduced-motion or when no
    // transition runs, which would leave the modal stuck open. The timeout is
    // the floor. Ignore transitions bubbling up from child elements.
    this.finishClose = (event) => {
      if (event && event.target !== this.dialogTarget) return
      this.cancelClose()
      if (this.dialogTarget.open) this.dialogTarget.close()
    }
    this.dialogTarget.addEventListener("transitionend", this.finishClose)
    this.closeFallback = setTimeout(this.finishClose, 400)
  }

  // Tear down an in-flight close so it can't fire after the drawer is reopened.
  cancelClose() {
    clearTimeout(this.closeFallback)
    if (this.finishClose) {
      this.dialogTarget.removeEventListener("transitionend", this.finishClose)
      this.finishClose = null
    }
  }

  // Wired to the dialog `close` event: park offscreen for the next open, and —
  // when opted in — return contents to a pristine state.
  closed() {
    this.dialogTarget.classList.add("translate-x-full")
    if (!this.resetOnCloseValue) return

    this.element.querySelectorAll("form").forEach((form) => form.reset())
    this.element.querySelectorAll("[data-drawer-reset]").forEach((slot) => {
      slot.innerHTML = ""
    })
  }
}
